// ---------------------------------------------------------
// Datei: PhaseScreen.tsx
// Zweck: Anzeige und Verwaltung einer Projektphase mit Dokumenten
// Besonderheiten:
// - Verwendet DocumentChecklist für Anzeige der Dokumente
import { getEnv } from "../utils/env";
// - Sticky Header mit Progress-Bar
// - CaptureModal für Dokumenten-Upload
// - Mobile-First mit Safe-Areas
// - Zentraler Document-Store für State-Persistenz über Navigation
// - ThemeToggle für Light/Dark Mode (Sprint 2 Task 1)
// - NEU: Make.com Webhook Upload für Arbeitsberichte (nur Bericht-Phase)
// Stand: 02.02.2026
// ---------------------------------------------------------

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueue } from '../contexts/QueueContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getPhase, getPhases, updatePhase } from '../services/api';
import { getDealById, updateDealTextProperty } from '../services/hubspotBackendClient';
import { useDealsStore } from '../store/dealsStore';
import { mapDealToPhases } from '../services/hubspotDealMapper';
import CaptureModal from '../components/CaptureModal';
import SyncStatus from '../components/SyncStatus';
import DocumentChecklist from '../components/DocumentChecklist';
import { DealContact, HubSpotDeal } from '../types/hubspot';
import { useDocumentStore } from '../store/documentState';
import { DocumentType, Phase } from '../types/documents';
import FileUploadModal, { UploadFile } from '../components/FileUploadModal';
import { stripUploadedDocumentFilesRuntimeData } from '../utils/uploadedDocumentFiles';
import AppHeader from '../components/AppHeader';
// NEU: Make.com Webhook Upload Komponente (NUR für Arbeitsberichte)
// NEU: Allgemeine Uploads (Issue #62) – generischer Foto-Upload ohne Phasenzuordnung
import AllgemeineUploads from '../components/AllgemeineUploads';
// NEU: Störungen-Formular (Issue #68) – Foto + Text → Make.com Webhook → HubSpot-Ticket
import StoerungenFormular from '../components/StoerungenFormular';
import AbschlussberichtWizard from '../components/AbschlussberichtWizard';
import ProjektdokumentationWizard from '../components/ProjektdokumentationWizard';
import SeriennummerModal from '../components/SeriennummerModal';
import SeriennummerRegistrierenModal from '../components/SeriennummerRegistrierenModal';
import PhaseFooterNav from '../components/PhaseFooterNav';


const PhaseScreen: React.FC = () => {
  // URL-Parameter extrahieren
  const { phaseId, projectId } = useParams<{ phaseId: string; projectId: string }>();
  const navigate = useNavigate();
  const { addToQueue } = useQueue();
  const { logout } = useAuth();
  const { showToast } = useToast();
  
  // Document-Store für persistenten Zustand
  const { updateDocumentStatus, getPhaseProgress, updatePhaseStatus, saveDocumentData, getDocumentData, getDocumentFiles } = useDocumentStore();
  const storedPhaseStatus = useDocumentStore((state) =>
    phaseId ? state.phases[phaseId] : undefined
  );

  // Deals aus dem persistierten Store (offline-fähig, kein erneuter API-Aufruf bei Navigation)
  const { upsertDeal } = useDealsStore();
  
  // State-Definitionen
  const [phase, setPhase] = useState<Phase | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showCaptureModal, setShowCaptureModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentType | null>(null);
  const [completing, setCompleting] = useState<boolean>(false);
  const [reloadKey, setReloadKey] = useState<number>(0);
  const [completionCountdown, setCompletionCountdown] = useState<number | null>(null);
  const [completionNavTarget, setCompletionNavTarget] = useState<string | null>(null);
  const prevProgressCompleteRef = useRef(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState<boolean>(false);
  // NEU: Deal Name für Webhook Upload (für Bericht-Phase und VOT-Phase relevant)
  const [dealName, setDealName] = useState<string>('');
  const [gdriveFolderId, setGdriveFolderId] = useState<string | undefined>(undefined);
  const [dealContact, setDealContact] = useState<DealContact | undefined>(undefined);
  const [showSeriennummerModal, setShowSeriennummerModal] = useState<boolean>(false);
  const [showSeriennummerRegistrierenModal, setShowSeriennummerRegistrierenModal] = useState<boolean>(false);
  const [seriennummerRegistrierenInitialValue, setSeriennummerRegistrierenInitialValue] = useState<string>('');
  // Alle Phasen des aktuellen Projekts (für Footer-Direktnavigation, Issue #16)
  const [allPhases, setAllPhases] = useState<Array<{ _id: string; name: string }>>([]);

  // Phase laden
  useEffect(() => {
    let unmounted = false;
    const fetchPhase = async () => {
      if (!phaseId) {
        navigate('/projects');
        return;
      }
      
      try {
        setLoading(true);
        setGdriveFolderId(undefined);

        // Prüfe ob es eine HubSpot-Phase ist (Format: dealId-phaseName)
        const isHubSpotPhase = phaseId.includes('-') && (
          phaseId.endsWith('-projektdokumentation') ||
          phaseId.endsWith('-durchfuehrung') ||
          phaseId.endsWith('-inbetriebnahme') ||
          phaseId.endsWith('-abschlussbericht') ||
          phaseId.endsWith('-uploads') ||
          phaseId.endsWith('-stoerungen')
        );
        
        if (isHubSpotPhase) {
          // HubSpot-Phase: Lade Deal-Daten aus Cache (kein API-Aufruf bei Navigation)
          const dealId = phaseId.split('-')[0];
          const deals = useDealsStore.getState().deals;
          let deal: HubSpotDeal | null | undefined = deals.find(d => d.id === dealId);
          if (!deal || !('vaillant_api_consent_angefragt' in deal)) {
              const fetchedDeal = await getDealById(dealId);
            if (fetchedDeal) {
              deal = fetchedDeal;
              upsertDeal(fetchedDeal);
            }
          }
          
          if (!deal) {
            throw new Error('Projekt nicht gefunden');
          }

          
          // Mappe Deal zu Phasen und finde die gewünschte Phase
          const phases = mapDealToPhases(deal);
          // Alle Phasen für Footer-Direktnavigation merken (Issue #16)
          if (!unmounted) {
            setAllPhases(phases.map(p => ({ _id: p._id, name: p.name })));
          }
          const targetPhase = phases.find(p => p._id === phaseId);
          
          if (!targetPhase) {
            throw new Error('Phase nicht gefunden');
          }
          
          if (unmounted) return;
          
          const latestStoredPhaseStatus = useDocumentStore.getState().phases[phaseId];

          // Setze Phase-Daten (HubSpot-Format ist kompatibel mit Phase-Interface)
          setPhase({
            _id: targetPhase._id,
            name: targetPhase.name,
            description: targetPhase.description,
            status: ((latestStoredPhaseStatus?.status || targetPhase.status) === 'completed' ? 'completed' : 'pending') as 'pending' | 'completed',
            project: targetPhase.project,
            completedAt: latestStoredPhaseStatus?.completedAt || targetPhase.completedAt,
            completedByEmail: phaseId.endsWith('-projektdokumentation')
              ? targetPhase.completedByEmail
              : latestStoredPhaseStatus?.completedByEmail || targetPhase.completedByEmail,
            approvedByEmail: targetPhase.approvedByEmail,
          });
          
          // NEU: Deal Name für Webhook Upload speichern
          setDealName(deal.dealname || '');
          setGdriveFolderId(deal.gdrive_folder_id || undefined);
          if (deal.contact) setDealContact(deal.contact);
          
          // WICHTIG: Store befüllen BEVOR DocumentChecklist rendert
          const { extractDocumentStatusWithTypes, calculatePhaseProgress } = await import('../services/hubspotDealMapper');
          const documents = extractDocumentStatusWithTypes(deal, phaseId);
          const progress = calculatePhaseProgress(phaseId, deal);
          
          console.log('[PhaseScreen] Befülle Store mit', documents.length, 'Dokumenten für', phaseId);
          const { useDocumentStore: documentStore } = await import('../store/documentState');
          const { setHubSpotDocumentStatus } = documentStore.getState();
          setHubSpotDocumentStatus(phaseId, documents, progress);
        } else {
          // Normale Backend-Phase
          const timeoutMs = 9000;
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Zeitüberschreitung beim Laden der Phase')), timeoutMs)
          );

          const phaseData: any = await Promise.race([getPhase(phaseId), timeoutPromise]);
          if (unmounted) return;
          
          // Typensichere Phase-Daten
          if (phaseData && typeof phaseData === 'object') {
            setPhase({
              _id: phaseData._id || '',
              name: phaseData.name || '',
              description: phaseData.description || '',
              status: phaseData.status || 'pending',
              project: {
                _id: phaseData.project?._id || projectId || '',
                name: phaseData.project?.name || ''
              },
              completedAt: phaseData.completedAt,
              completedByEmail: phaseData.completedByEmail,
              approvedByEmail: phaseData.approvedByEmail
            });
          } else {
            throw new Error('Ungültiges Phasendaten-Format');
          }

          // Phasenliste fürs Footer-Nav nachladen (best effort, blockiert nicht) – Issue #16
          const projectIdForList = (phaseData as any)?.project?._id || projectId;
          if (projectIdForList) {
            try {
              const phasesList: any = await getPhases(projectIdForList);
              if (!unmounted && Array.isArray(phasesList)) {
                setAllPhases(phasesList.map((p: any) => ({ _id: p._id, name: p.name })));
              }
            } catch (listErr) {
              console.warn('Phasenliste für Footer-Nav konnte nicht geladen werden:', listErr);
            }
          }
        }
      } catch (err: any) {
        if (unmounted) return;
        setError(err.message || 'Fehler beim Laden der Phase');
        console.error('Fehler beim Laden der Phase:', err);
      } finally {
        if (!unmounted) setLoading(false);
      }
    };

    fetchPhase();
    return () => {
      unmounted = true;
    };
  }, [phaseId, projectId, navigate, reloadKey, upsertDeal]);

  useEffect(() => {
    if (!storedPhaseStatus || !phase) {
      return;
    }

    const isCurrentProjektdokumentationPhase = phaseId?.endsWith('-projektdokumentation') || false;

    if (
      phase.status !== storedPhaseStatus.status ||
      phase.completedAt !== storedPhaseStatus.completedAt ||
      (!isCurrentProjektdokumentationPhase && phase.completedByEmail !== storedPhaseStatus.completedByEmail)
    ) {
      setPhase((previous) =>
        previous
          ? {
              ...previous,
              status: storedPhaseStatus.status,
              completedAt: storedPhaseStatus.completedAt,
              completedByEmail: isCurrentProjektdokumentationPhase
                ? previous.completedByEmail
                : storedPhaseStatus.completedByEmail,
            }
          : previous
      );
    }
  }, [phase, phaseId, storedPhaseStatus]);

  // Phase-Typ-Flags und Progress – werden von useEffects und JSX genutzt
  const isAbschlussberichtPhase = phaseId?.endsWith('-abschlussbericht') || false;
  const isProjektdokumentationPhase = phaseId?.endsWith('-projektdokumentation') || false;
  const isDurchfuehrungPhase = phaseId?.endsWith('-durchfuehrung') || false;
  const isHubSpotPhase = phaseId?.includes('-') && (
    phaseId.endsWith('-projektdokumentation') ||
    phaseId.endsWith('-durchfuehrung') ||
    phaseId.endsWith('-inbetriebnahme') ||
    phaseId.endsWith('-abschlussbericht') ||
    phaseId.endsWith('-uploads') ||
    phaseId.endsWith('-stoerungen')
  ) || false;
  const isUploadsPhase = phaseId?.endsWith('-uploads') || false;
  const isStoerungenPhase = phaseId?.endsWith('-stoerungen') || false;
  const isDocumentChecklistPhase = !isUploadsPhase && !isStoerungenPhase && !isAbschlussberichtPhase && !isProjektdokumentationPhase;
  const dealId = phaseId?.split('-')[0] || '';
  const progress = phaseId ? getPhaseProgress(phaseId) : { completed: 0, required: 0 };
  const isPhaseCompleteByProgress = progress.required > 0 && progress.completed >= progress.required;
  const isPhaseReadOnly = phase?.status === 'completed' && isPhaseCompleteByProgress;

  // Auto-Abschluss: Nur auslösen wenn isPhaseCompleteByProgress false→true wechselt
  useEffect(() => {
    const wasComplete = prevProgressCompleteRef.current;
    prevProgressCompleteRef.current = isPhaseCompleteByProgress;

    if (
      !wasComplete &&
      isPhaseCompleteByProgress &&
      phase?.status !== 'completed' &&
      !completing &&
      isDocumentChecklistPhase &&
      !loading
    ) {
      completePhase();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPhaseCompleteByProgress]);

  // Countdown: jede Sekunde dekrementieren → Navigation bei 0
  useEffect(() => {
    if (completionCountdown === null) return;
    if (completionCountdown <= 0) {
      const target = completionNavTarget;
      setCompletionCountdown(null);
      setCompletionNavTarget(null);
      if (target) navigate(target, { replace: true });
      return;
    }
    const timer = setTimeout(() => {
      setCompletionCountdown(c => (c !== null ? c - 1 : null));
    }, 1000);
    return () => clearTimeout(timer);
  }, [completionCountdown, completionNavTarget, navigate]);

  const handleCancelCompletion = () => {
    setCompletionCountdown(null);
    setCompletionNavTarget(null);
  };

  const retryLoad = () => {
    setError(null);
    setReloadKey((k) => k + 1);
  };

  const navigateToProjectsOverview = () => {
    navigate('/projects', { replace: true });
  };

  const navigateToPhaseOverview = () => {
    navigate(projectId ? `/projects/${projectId}` : '/projects', { replace: true });
  };

  // HubSpot Deal-ID und Phase-Typ-Flags werden oben (vor den useEffects) deklariert

  // Dokument für Upload auswählen
  const handleDocumentSelect = (document: DocumentType) => {
    setSelectedDocument(document);
  
    if (document._id === 'seriennummer-ausseneinheit') {
      setShowSeriennummerModal(true);
    } else if (document._id === 'seriennummer-registrieren') {
      const sn = (document as any).fileId
        || getDocumentData(`${dealId}-inbetriebnahme`, 'seriennummer-registrieren-text')
        || '';
      setSeriennummerRegistrierenInitialValue(sn);
      setShowSeriennummerRegistrierenModal(true);
    } else if (isDurchfuehrungPhase) {
      setShowFileUploadModal(true);
    } else {
      setShowCaptureModal(true);
    }
  };

  // Bild nach Aufnahme verarbeiten (für HubSpot - NICHT für Arbeitsberichte)
  const handleImageCapture = async (imageData: string) => {
    if (!selectedDocument) return;
    
    try {
      // ✅ B-03: Optimistic Update SOFORT (bevor API-Call)
      if (phaseId) {
        updateDocumentStatus(phaseId, selectedDocument._id, 'completed');
      }

      // ✅ B-03: Success-Toast sofort anzeigen
      showToast('success', `${selectedDocument.name} hochgeladen ✅`, 2000);
      
      // Bild in Queue für Upload hinzufügen (asynchron im Hintergrund)
      // Queue-Service uploaded zu Google Drive (mit dealName) oder Fallback HubSpot
      addToQueue('document_upload', {
        imageData,
        documentId: selectedDocument._id,
        phaseId: phaseId || '',
        projectId: projectId || '',
        dealName: dealName || '',
        sharedDrive: getEnv('VITE_GOOGLE_SHARED_DRIVE_NAME') || undefined,
        gdriveFolderId: gdriveFolderId,
      });
      
      // Modal schließen
      setShowCaptureModal(false);
      setSelectedDocument(null);
      
    } catch (err: any) {
      // ✅ B-03: Rollback bei Fehler (zurück auf 'pending')
      if (phaseId && selectedDocument) {
        updateDocumentStatus(phaseId, selectedDocument._id, 'pending');
      }
      
      // Error-Toast
      showToast('error', 'Upload fehlgeschlagen ⚠️', 4000);
      setError(err.message || 'Fehler bei der Bildverarbeitung');
    }
  };

  // Phase abschließen
  const completePhase = async () => {
    if (!phase || !phaseId) return;
    
    // Finale Progress-Prüfung (Guard gegen Race Conditions)
    const finalProgress = getPhaseProgress(phaseId);
    if (finalProgress.completed < finalProgress.required) {
      setError(`Noch nicht alle Pflichtdokumente hochgeladen (${finalProgress.completed}/${finalProgress.required})`);
      return;
    }
    
    try {
      setCompleting(true);
      
      const completedAt = new Date().toISOString();
      
      // HubSpot-Phasen sind virtuelle Phasen-IDs (dealId-phaseKey) und haben
      // keinen dedizierten PUT /api/phases/:id Backend-Endpunkt.
      // Für klassische DB-Phasen bleibt der API-Call aktiv.
      if (!isHubSpotPhase) {
        await updatePhase(phaseId, { status: 'completed', completedAt });
      }
      
      // ✅ B-02: Store-Update für Phase-Status (Persistenz!)
      updatePhaseStatus(phaseId, 'completed', completedAt);
      
      // UI aktualisieren
      setPhase({ ...phase, status: 'completed', completedAt, completedByEmail: undefined });
      
      // Clear errors
      setError(null);

      // Nächste Phase oder Projektliste bestimmen
      const currentIndex = allPhases.findIndex(p => p._id === phaseId);
      const nextPhase = allPhases[currentIndex + 1];
      const isLastPhase = !nextPhase;
      const navTarget = isLastPhase
        ? '/projects'
        : `/projects/${projectId}/phase/${nextPhase._id}`;

      if (isLastPhase) {
        showToast('success', 'Projekt vollständig dokumentiert! 🏆', 5000);
      }

      // Countdown-Toast starten (3s → dann Navigation)
      setCompletionNavTarget(navTarget);
      setCompletionCountdown(3);
      
    } catch (err: any) {
      setError(err.message || 'Fehler beim Abschließen der Phase');
      // ✅ B-02: Error-Toast
      showToast('error', err.message || 'Fehler beim Abschließen der Phase', 4000);
      // ✅ BUGFIX: Also scroll to top on error so user sees error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setCompleting(false);
    }
  };

  // Progress, isPhaseCompleteByProgress, isPhaseReadOnly werden oben (vor den useEffects) deklariert

  // Fehlerbehandlung für DocumentChecklist
  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  // Multi-Upload: Dateien verarbeiten (Legacy - für HubSpot Dokumente)
  const handleFileUpload = async (files: UploadFile[], documentOverride?: DocumentType) => {
    const targetDocument = documentOverride || selectedDocument;
    if (!targetDocument || !phaseId) return;
    
    try {
      // Status sofort als completed markieren (optimistic update)
      updateDocumentStatus(phaseId, targetDocument._id, 'completed');

      // Ein Queue-Item mit mehreren Dateien verhindert parallele Patch-Rennen.
      addToQueue('document_upload', {
        files: files.map((file) => ({
          id: file.id,
          name: file.name,
          type: file.type,
          size: file.size,
          dataUrl: file.dataUrl,
        })),
        documentId: targetDocument._id,
        phaseId,
        projectId,
        dealName: dealName || '',
        existingFiles: stripUploadedDocumentFilesRuntimeData(getDocumentFiles(phaseId, targetDocument._id)),
        sharedDrive: getEnv('VITE_GOOGLE_SHARED_DRIVE_NAME') || undefined,
        gdriveFolderId: gdriveFolderId,
      });
      
      showToast('success', `${files.length} Datei(en) hochgeladen ✅`, 2000);
    } catch (err) {
      console.error('Upload-Fehler:', err);
      showToast('error', 'Upload fehlgeschlagen', 3000);
    }
    
    setShowFileUploadModal(false);
    setSelectedDocument(null);
  };

  const handleSeriennummerSubmit = async (serialNumber: string, imageData: string | null) => {
    setShowSeriennummerModal(false);
    setSelectedDocument(null);

    const durchfuehrungPhaseId = `${dealId}-durchfuehrung`;

    try {
      console.log('[Seriennummer] dealId:', dealId, '| serialNumber:', serialNumber);
      await updateDealTextProperty(dealId, 'seriennummer_der_warmepumpe', serialNumber);

      saveDocumentData(durchfuehrungPhaseId, 'seriennummer-ausseneinheit-text', serialNumber);

      if (imageData) {
        addToQueue('document_upload', {
          imageData,
          documentId: 'seriennummer-ausseneinheit',
          phaseId: durchfuehrungPhaseId,
          projectId: projectId || '',
          dealName: dealName || '',
          sharedDrive: getEnv('VITE_GOOGLE_SHARED_DRIVE_NAME') || undefined,
          gdriveFolderId: gdriveFolderId,
        });
      }

      updateDocumentStatus(durchfuehrungPhaseId, 'seriennummer-ausseneinheit', 'completed');
      showToast('success', 'Seriennummer gespeichert ✅', 2000);
    } catch (err: any) {
      console.error('[Seriennummer] Fehler:', err?.response?.data ?? err?.message ?? err);
      showToast('error', 'Seriennummer konnte nicht gespeichert werden', 4000);
    }
  };

  const handleSeriennummerRegistrierenSubmit = async (serialNumber: string) => {
    setShowSeriennummerRegistrierenModal(false);
    setSelectedDocument(null);
    const inbetriebnahmePhaseId = `${dealId}-inbetriebnahme`;
    try {
      await updateDealTextProperty(dealId, 'seriennummer_der_warmepumpe', serialNumber);
      saveDocumentData(inbetriebnahmePhaseId, 'seriennummer-registrieren-text', serialNumber);

      const payload: { serial_number: string; email?: string } = { serial_number: serialNumber };
      if (dealContact?.email) payload.email = dealContact.email;

      const res = await fetch('/api/vaillant/register-serial-number', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json() as { success: boolean; statusCode?: number; response?: { detail?: string } };

      if (!data.success) {
        const detail = data.response?.detail ?? 'Ungültige Seriennummer';
        showToast('error', `Registrierung fehlgeschlagen: ${detail}`, 5000);
        return;
      }

      const consentTimestamp = new Date().toISOString();
      await updateDealTextProperty(dealId, 'vaillant_api_consent_angefragt', consentTimestamp);
      saveDocumentData(inbetriebnahmePhaseId, 'seriennummer-registrieren-text', consentTimestamp);

      const cachedDeal = useDealsStore.getState().deals.find(d => d.id === dealId);
      if (cachedDeal) upsertDeal({ ...cachedDeal, vaillant_api_consent_angefragt: consentTimestamp });

      updateDocumentStatus(inbetriebnahmePhaseId, 'seriennummer-registrieren', 'completed');
      showToast('success', 'Seriennummer erfolgreich registriert ✅', 3000);
    } catch (err: any) {
      console.error('[SeriennummerRegistrieren] Fehler:', err?.response?.data ?? err?.message ?? err);
      showToast('error', 'Seriennummer konnte nicht registriert werden', 4000);
    }
  };

  // UI-Elemente
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 overflow-x-hidden">
      {/* Header */}
      <AppHeader
        title={loading ? 'Lade Phase...' : phase?.name || 'Phase'}
        meta={phase?.project ? <span>{phase.project.name}</span> : undefined}
        backAction={{ onClick: navigateToPhaseOverview, ariaLabel: 'Zurück zur Phasen-Übersicht' }}
        actions={phase ? (
          phase.status === 'completed' ? (
            <span className="rounded-full bg-green-100 px-3 py-1.5 text-xs font-medium text-green-800">
              Abgeschlossen
            </span>
          ) : (
            <span className="rounded-full bg-blue-100 px-3 py-1.5 text-xs font-medium text-blue-800">
              In Bearbeitung
            </span>
          )
        ) : null}
        menuContent={(
          <>
            <button
              onClick={navigateToProjectsOverview}
              className="mt-1 flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-slate-700 transition hover:bg-slate-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Zur Projekt-Übersicht
            </button>
            <button
              onClick={logout}
              className="flex w-full items-center rounded-xl px-3 py-3 text-left text-sm text-red-600 transition hover:bg-red-50"
            >
              <svg className="mr-3 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Abmelden
            </button>
          </>
        )}
        containerClassName="mx-auto max-w-[430px] px-4 py-4"
        useDesktopLogoOffset={false}
      />

      {/* Progress-Bar */}
      {progress.required > 0 && (
        <div className={`p-2 ${isPhaseCompleteByProgress ? 'bg-green-50 dark:bg-green-900/30' : 'bg-gray-50 dark:bg-gray-700'}`}>
          <div className="mx-auto flex max-w-[430px] items-center justify-between px-4">
            <span className={`text-sm font-medium flex items-center gap-1.5 ${isPhaseCompleteByProgress ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
              {isPhaseCompleteByProgress && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              )}
              Pflichtdokumente: {progress.completed}/{progress.required}
            </span>
            <div className="w-32 rounded-full bg-gray-200 h-2.5 dark:bg-gray-600">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${isPhaseCompleteByProgress ? 'bg-green-500' : 'bg-primary-600'}`}
                style={{
                  width: `${(progress.completed / progress.required) * 100}%`
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Hauptinhalt – pb berücksichtigt PhaseFooterNav (5rem) + Safe-Area + 1rem Buffer.
          BottomNavigation ist auf Phase-Screens ausgeblendet (App.tsx), darum kein 4rem-Aufschlag. */}
      <main className="max-w-[430px] mx-auto px-4 py-6 pb-[calc(env(safe-area-inset-bottom)+6rem)]">
        {/* Readonly-Banner für abgeschlossene Phasen */}
        {isPhaseReadOnly && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 mr-3 mt-0.5 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-green-800 mb-1">
                  Phase abgeschlossen
                </h3>
                <p className="text-sm text-green-700">
                  {isDocumentChecklistPhase
                    ? 'Diese Phase wurde erfolgreich abgeschlossen. Fotos und Dokumente können weiterhin bearbeitet werden.'
                    : 'Diese Phase wurde erfolgreich abgeschlossen. Uploads sind nicht mehr möglich.'}
                  {phase.completedAt && (
                    <span className="block mt-1 text-xs text-green-600">
                      Abgeschlossen am: {new Date(phase.completedAt).toLocaleString('de-DE')}
                    </span>
                  )}
                  {phase.completedByEmail?.trim() && (
                    <span className="block mt-1 text-xs text-green-600">
                      Abgeschlossen von: {phase.completedByEmail.trim()}
                    </span>
                  )}
                  {phase.approvedByEmail && (
                    <span className="block mt-1 text-xs text-green-600">
                      Freigegeben von: {phase.approvedByEmail}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Fehleranzeige */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 mb-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>{error}</span>
              </div>
              <div className="flex gap-2">
                <button 
                  className="text-sm px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
                  onClick={navigateToPhaseOverview}
                >
                  Zurück
                </button>
                <button 
                  className="text-sm px-3 py-1 rounded bg-primary-600 hover:bg-primary-700 text-white"
                  onClick={retryLoad}
                >
                  Erneut versuchen
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading-State */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
          </div>
        ) : (
          <>
            {/* Phasenbeschreibung */}
            {phase?.description && !isProjektdokumentationPhase && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Beschreibung
                </h2>
                <p className="text-gray-600 dark:text-gray-300">
                  {phase.description}
                </p>
              </div>
            )}

            {/* Allgemeine Uploads Phase (Issue #62) */}
            {isUploadsPhase ? (
              <AllgemeineUploads dealName={dealName} gdriveFolderId={gdriveFolderId} />
            ) : isStoerungenPhase ? (
              <StoerungenFormular dealName={dealName} hubspotId={dealId} />
            ) : isProjektdokumentationPhase ? (
              <ProjektdokumentationWizard
                projectId={projectId || dealId}
                phaseId={phaseId || ''}
                disabled={isPhaseReadOnly}
              />
            ) : isAbschlussberichtPhase ? (
              <AbschlussberichtWizard
                projectId={projectId || dealId}
                phaseId={phaseId || ''}
                disabled={isPhaseReadOnly}
              />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white p-4 border-b dark:border-gray-700">
                  Dokumente
                </h2>
                
	                <DocumentChecklist
	                  phaseId={phaseId || ''}
	                  onSelectDocument={handleDocumentSelect}
	                  onDirectPhotoUpload={(document, files) => handleFileUpload(files, document)}
	                  onError={handleError}
	                  disabled={false}
                  dealContact={dealContact}
                />

              </div>
            )}

            {/* Auto-Abschluss läuft: Spinner anzeigen während completePhase() verarbeitet */}
            {isDocumentChecklistPhase && completing && (
              <div className="mt-8 flex justify-center items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500"></div>
                Wird abgeschlossen...
              </div>
            )}
          </>
        )}
      </main>

      {/* CaptureModal für Dokumenten-Upload (HubSpot - alle außer Arbeitsbericht) */}
      {showCaptureModal && (
        <CaptureModal
          onClose={() => {
            setShowCaptureModal(false);
            setSelectedDocument(null);
          }}
          onCapture={handleImageCapture}
          allowedTypes={selectedDocument?.allowedTypes || ['image/jpeg', 'image/png']}
          title={`${selectedDocument?.name || 'Dokument'} hinzufügen`}
          dealName={dealName}
          phaseId={phaseId}
          projectId={projectId}
          checklistItemId={selectedDocument?._id}
        />
      )}

      {/* SeriennummerModal für Seriennummer der Außeneinheit */}
      {showSeriennummerModal && (
        <SeriennummerModal
          onClose={() => {
            setShowSeriennummerModal(false);
            setSelectedDocument(null);
          }}
          onSubmit={handleSeriennummerSubmit}
        />
      )}

      {/* SeriennummerRegistrierenModal für Inbetriebnahme-Phase */}
      {showSeriennummerRegistrierenModal && (
        <SeriennummerRegistrierenModal
          onClose={() => {
            setShowSeriennummerRegistrierenModal(false);
            setSelectedDocument(null);
          }}
          onSubmit={handleSeriennummerRegistrierenSubmit}
          initialValue={seriennummerRegistrierenInitialValue}
        />
      )}

      {/* FileUploadModal für Legacy Multi-Upload (falls benötigt für andere Dokumente) */}
      {showFileUploadModal && selectedDocument && (
        <FileUploadModal
          isOpen={showFileUploadModal}
          onClose={() => {
            setShowFileUploadModal(false);
            setSelectedDocument(null);
          }}
          onUpload={handleFileUpload}
          title={`${selectedDocument.name} hinzufügen`}
          allowedTypes={isDurchfuehrungPhase ? ['image/*'] : selectedDocument.allowedTypes || ['application/pdf']}
          multiUpload={true}
          nativePickerOnly={isDurchfuehrungPhase}
          documentId={selectedDocument._id}
          phaseId={phaseId}
          projectId={projectId}
        />
      )}

      {/* Phasen-Direktnavigation (Issue #16) – sitzt über der globalen BottomNavigation */}
      {phaseId && projectId && allPhases.length > 0 && (
        <PhaseFooterNav
          phases={allPhases}
          currentPhaseId={phaseId}
          projectId={projectId}
        />
      )}

      {/* Countdown-Toast nach Auto-Abschluss */}
      {completionCountdown !== null && (
        <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+80px)] left-0 right-0 flex justify-center z-[60] px-4 sm:bottom-[calc(env(safe-area-inset-bottom)+16px)]">
          <div className="bg-green-500 text-white rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[280px] max-w-[400px] animate-slide-up">
            <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <div className="flex-1 text-sm font-medium">
              Phase abgeschlossen! Weiter in {completionCountdown}s...
            </div>
            <button
              onClick={handleCancelCompletion}
              className="flex-shrink-0 bg-white/20 hover:bg-white/30 rounded px-2 py-1 text-xs font-semibold transition-colors"
            >
              Abbrechen
            </button>
          </div>
        </div>
      )}

      {/* Sync-Status-Anzeige */}
      <SyncStatus />
    </div>
  );
};

export default PhaseScreen;
